import React, { useState, useEffect, useCallback } from 'react';
import { Car, Loader, RefreshCw, ShieldAlert, ExternalLink, AlertTriangle, User, MapPin, Send, CheckCircle, Clock, X } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { ACCIDENT_FIELDS, fieldByKey, missingForClaim, valueOf, hitLabel } from '../utils/accidentClaim';

// ── Driver Accidents (engine view) ────────────────────────────────────────────
// Accidents drivers report from the portal (My Van → Accidents) land in
// driver_records. Until this page there was NO admin view of them at all — the
// office could not see a single accident report a driver filed, which is how a real
// claim (EUU35K, 17 Jul) sat unseen. This surfaces them with their photos.
//
// driver-records-sb scopes a portal driver to their own records but lets staff
// (siteLogin) list any/all — so passing no driver returns every accident.

const RECORDS_URL = '/.netlify/functions/driver-records-sb';

const recApi = (action, payload = {}) =>
  fetch(RECORDS_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());

const fmtDate = (iso) => (iso ? new Date(iso + 'T00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtWhen = (ts) => (ts ? new Date(ts).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export default function DriverAccidentsPage() {
  const { personnel, vehicles, currentUser, addNotification } = useApp();
  const isAdmin = currentUser?.role === 'admin';
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await recApi('listDriverRecords', { recordType: 'accident' });
      if (r.success) setRecords(r.records || []);
      else addNotification?.(`Could not load accidents: ${r.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setLoading(false); }
  }, [addNotification]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const nameFor = (login) => (personnel || []).find((p) => p.loginName?.toUpperCase() === (login || '').toUpperCase())?.name || login;
  // The portal's accident form never captured a rego, so resolve the driver's vehicle
  // from the fleet. Shown as a best guess, not asserted as fact.
  const vehicleFor = (login) => (vehicles || []).find((v) => (v.login || '').toUpperCase() === (login || '').toUpperCase());

  if (!isAdmin) {
    return (
      <>
        <div className="page-header"><div className="page-header-left"><h2>Driver Accidents</h2></div></div>
        <div className="page-body">
          <div className="card" style={{ padding: 28, display: 'flex', alignItems: 'center', gap: 12 }}>
            <ShieldAlert size={22} color="var(--hp-gold)" />
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Admins only</div>
              <div style={{ color: 'var(--hp-dim)', fontSize: '.88rem' }}>Accident reports are restricted to Paul and Jayda.</div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const noPhotos = records.filter((r) => !(r.documents || []).length).length;
  const notReady = records.filter((r) => missingForClaim(r).length).length;

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Car size={22} style={{ color: 'var(--hp-gold)' }} /> Driver Accidents</h2>
          <p>{records.length} reported{noPhotos ? ` · ${noPhotos} without photos` : ''}{notReady ? ` · ${notReady} missing claim details` : ''} — as filed by drivers from the portal</p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="card" style={{ padding: 26, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading accidents…</div>
        ) : records.length === 0 ? (
          <div className="card" style={{ padding: 26, color: 'var(--hp-dim)' }}>No accidents reported.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {records.map((r) => {
              const photos = r.documents || [];
              const veh = vehicleFor(r.driver);
              const op = (r.meta && r.meta.otherParty) || {};
              return (
                <div key={r.id} className="card" style={{ padding: 18 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 260 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 800, fontSize: '1rem' }}>{fmtDate(r.date)}</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.86rem', color: 'var(--hp-dim)' }}>
                          <User size={13} /> {nameFor(r.driver)} <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.78rem' }}>({r.driver})</span>
                        </span>
                        {veh && (
                          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--hp-gold)', fontSize: '.86rem' }}>
                            {veh.rego} <span style={{ color: 'var(--hp-dim)', fontWeight: 400 }}>{[veh.make, veh.model].filter(Boolean).join(' ')}</span>
                          </span>
                        )}
                        {r.status && r.status !== 'open' && <span style={{ fontSize: '.75rem', color: 'var(--hp-dim)' }}>{r.status}</span>}
                      </div>
                      {r.location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.84rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
                          <MapPin size={13} /> {r.location}
                        </div>
                      )}
                      <div style={{ fontSize: '.88rem', color: 'var(--hp-text)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{r.description || '—'}</div>

                      {(op.name || op.rego || op.phone || op.insurer || op.licence) && (
                        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, background: 'var(--hp-surface)', fontSize: '.84rem' }}>
                          <div style={{ fontSize: '.7rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>Other party</div>
                          <div style={{ color: 'var(--hp-text)' }}>
                            {[op.name, op.rego, op.phone].filter(Boolean).join(' · ') || '—'}
                          </div>
                          <div style={{ color: 'var(--hp-dim)', fontSize: '.78rem', marginTop: 3 }}>
                            {op.licence ? `Licence ${op.licence}` : ''}
                            {op.licence && op.insurer ? ' · ' : ''}
                            {op.insurer ? `Insurer ${op.insurer}` : <span style={{ color: 'var(--hp-amber, #f59e0b)' }}>{op.licence ? ' · ' : ''}insurer not captured</span>}
                          </div>
                        </div>
                      )}
                      <div style={{ fontSize: '.72rem', color: 'var(--hp-muted)', marginTop: 8 }}>Reported {fmtWhen(r.createdAt)}</div>
                      <ClaimSection r={r} onChanged={load} notify={addNotification} />
                    </div>
                  </div>

                  <div style={{ marginTop: 14, borderTop: '1px solid var(--hp-border)', paddingTop: 12 }}>
                    {photos.length === 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.84rem', color: 'var(--hp-amber, #f59e0b)' }}>
                        <AlertTriangle size={14} /> No photos attached — ask the driver to add them from the portal (My Van → Accidents → Add photos).
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>
                          {photos.length} photo{photos.length > 1 ? 's' : ''}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {photos.map((p, i) => (
                            <a key={i} href={p.viewUrl} target="_blank" rel="noreferrer" title={p.label || `Photo ${i + 1}`}>
                              <img
                                src={p.thumbUrl || p.viewUrl}
                                alt={p.label || `Photo ${i + 1}`}
                                style={{ width: 128, height: 128, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hp-border)' }}
                              />
                            </a>
                          ))}
                        </div>
                      </>
                    )}
                    {r.viewUrl && (
                      <a href={r.viewUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ marginTop: 10 }}>
                        <ExternalLink size={12} /> Open primary image
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: '.8rem', color: 'var(--hp-dim)' }}>
          <AlertTriangle size={13} /> The vehicle in the header is the driver's currently assigned van. Reports filed since 24 Sep 2026 carry the rego the driver entered — that one is under Claim details.
        </div>
      </div>
    </>
  );
}

// ── Claim details + "what's missing" + Request more info ──────────────────────
// Shows what the driver gave beyond the one-line summary, lists what an insurer will still
// ask for, and lets the office push exactly those questions to the driver's portal (red dot
// on My Van, a banner at the top of it, and a push to their phone if alerts are on).
const lab = { fontSize: '.7rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 };
const fmtStamp = (ts) => (ts ? new Date(ts).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '');

function ClaimSection({ r, onChanged, notify }) {
  const missing = missingForClaim(r);
  const req = (r.meta && r.meta.infoRequest) || null;
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(() => new Set());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  // Given details, in form order — skip what's shown above (address, description, the
  // Other party box) and the photos.
  const shown = ACCIDENT_FIELDS.filter((f) => !['address', 'description', 'photos', 'otherRego', 'otherName', 'otherPhone', 'otherLicence', 'otherInsurer'].includes(f.key))
    .map((f) => [f, valueOf(r, f.key)]).filter(([, v]) => v);

  const start = () => { setPick(new Set(missing)); setNote(''); setOpen(true); };
  const toggle = (k) => setPick((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const send = async () => {
    const fields = ACCIDENT_FIELDS.filter((f) => pick.has(f.key)).map((f) => ({ key: f.key, label: f.label }));
    if (!fields.length) { notify?.('Tick at least one detail to ask for', 'error'); return; }
    setBusy(true);
    try {
      const res = await recApi('requestAccidentInfo', { id: r.id, fields, note });
      if (!res.success) throw new Error(res.error || 'failed');
      const sent = res.push && res.push.sent;
      notify?.(sent ? `Sent to ${r.driver} — portal red dot + phone banner` : `Sent to ${r.driver} — red dot on their portal (their phone alerts are off, so no banner)`, 'success');
      setOpen(false); onChanged();
    } catch (e) { notify?.(`Could not send: ${e.message}`, 'error'); }
    setBusy(false);
  };
  const cancel = async () => {
    setBusy(true);
    try { const res = await recApi('cancelAccidentInfo', { id: r.id }); if (!res.success) throw new Error(res.error); onChanged(); }
    catch (e) { notify?.(`Could not cancel: ${e.message}`, 'error'); }
    setBusy(false);
  };

  const display = (f, v) => (f.key === 'hit' ? hitLabel(v) || v : f.kind === 'photo' ? 'attached' : v);

  return (
    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {shown.length > 0 && (
        <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--hp-surface)', fontSize: '.84rem' }}>
          <div style={lab}>Claim details</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, auto) 1fr', gap: '4px 14px' }}>
            {shown.map(([f, v]) => (
              <React.Fragment key={f.key}>
                <div style={{ color: 'var(--hp-dim)' }}>{f.label}</div>
                <div style={{ color: 'var(--hp-text)', whiteSpace: 'pre-wrap' }}>{display(f, v)}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {missing.length > 0 ? (
        <div style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.06)', fontSize: '.84rem' }}>
          <div style={{ ...lab, color: 'var(--hp-red)', display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={12} /> Missing for a claim · {missing.length}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {missing.map((k) => (
              <span key={k} style={{ fontSize: '.76rem', padding: '3px 8px', borderRadius: 999, border: '1px solid rgba(239,68,68,0.35)', color: 'var(--hp-text)' }}>{fieldByKey(k).label}</span>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.82rem', color: 'var(--hp-green)' }}><CheckCircle size={14} /> Has everything a claim needs</div>
      )}

      {req && req.status === 'pending' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: '.82rem', color: 'var(--hp-amber, #f59e0b)' }}>
          <Clock size={14} /> Asked {r.driver} for {req.fields.length} detail{req.fields.length === 1 ? '' : 's'} on {fmtStamp(req.requestedAt)} — waiting on the driver
          <button className="btn btn-ghost btn-sm" onClick={cancel} disabled={busy}><X size={12} /> Cancel request</button>
        </div>
      )}
      {req && req.status === 'answered' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.82rem', color: 'var(--hp-green)' }}>
          <CheckCircle size={14} /> {r.driver} answered on {fmtStamp(req.answeredAt)}
          {req.stillMissing && req.stillMissing.length ? <span style={{ color: 'var(--hp-dim)' }}> — left blank: {req.stillMissing.join(', ')}</span> : null}
        </div>
      )}

      {!open ? (
        <div>
          <button className="btn btn-primary btn-sm" onClick={start} disabled={busy}>
            <Send size={13} /> {req && req.status === 'pending' ? 'Change request' : 'Request more info'}
          </button>
        </div>
      ) : (
        <div style={{ padding: 12, borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)' }}>
          <div style={{ fontSize: '.84rem', fontWeight: 700, marginBottom: 4 }}>Ask {r.driver} for:</div>
          <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)', marginBottom: 10 }}>Pre-ticked with what's missing. They get a red dot on My Van, a banner at the top of it, and a push to their phone.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '6px 14px', marginBottom: 10 }}>
            {ACCIDENT_FIELDS.map((f) => (
              <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '.82rem', cursor: 'pointer', color: missing.includes(f.key) ? 'var(--hp-text)' : 'var(--hp-dim)' }}>
                <input type="checkbox" checked={pick.has(f.key)} onChange={() => toggle(f.key)} /> {f.label}
              </label>
            ))}
          </div>
          <textarea className="form-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Note to the driver (optional) — e.g. Go back and photograph the other car's plate if it's still there."
            style={{ width: '100%', marginBottom: 10, fontSize: '.84rem' }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={send} disabled={busy || !pick.size}><Send size={13} /> {busy ? 'Sending…' : `Send to ${r.driver}`}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
